import { inject } from '@angular/core';
import {
  AclAssetChildResourceType,
  AclAssetModulePermission,
  AclAssetModuleResourceType,
  AclAssetPermission,
  AclAssetResourceType,
  AclOrgSpacePermission,
  AclPermission,
  AclPermissionDetail,
  AclPermissionsRecord,
  AclStrategy,
} from '@y42/acl/models';
import { ErrorHandlerService } from '@y42/error-handling';
import { Y42FeatureType } from '@y42/json-schema';
import { isNullish } from '@y42/util/nullish';
import { OrgSpaceAclService } from '@y42/org-space/acl';
import { map, Observable, of, switchMap } from 'rxjs';
import { AbstractFeatureEntityState } from '../models/abstract-feature-entity-state.model';
import {
  Y42FeatureToAclAssetChildResourceType,
  Y42FeatureToAclAssetModuleResourceType,
  Y42FeatureToAclAssetResourceType,
} from '../models/feature-type-to-acl-resource-type.model';
import { AbstractFeatureQueryEntity } from '../state/abstract-feature-query-entity.service';
import { AssetAclService } from './asset-acl.service';
import { AssetModuleAclService } from './asset-module-acl.service';

export abstract class AbstractFeatureAclService<T extends Y42FeatureType = Y42FeatureType> {
  constructor(
    protected readonly cfg: {
      featureType: T;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      query: AbstractFeatureQueryEntity<AbstractFeatureEntityState<any, any>>;
    },
  ) {}

  private readonly assetAclService = inject(AssetAclService);
  private readonly assetModuleAclService = inject(AssetModuleAclService);
  private readonly orgSpaceAclService = inject(OrgSpaceAclService);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly featureType: T = this.cfg.featureType;
  private readonly query: AbstractFeatureQueryEntity = this.cfg.query;

  private readonly assetModuleResourceType: AclAssetModuleResourceType | null =
    Y42FeatureToAclAssetModuleResourceType[this.featureType];
  private readonly assetResourceType: AclAssetResourceType | null = Y42FeatureToAclAssetResourceType[this.featureType];
  private readonly assetChildResourceType: AclAssetChildResourceType | null =
    Y42FeatureToAclAssetChildResourceType[this.featureType];

  loadResourcesWithReadPermissions(): Observable<null> {
    const resourceType = this.assetResourceType;

    if (!resourceType) {
      console.warn(
        `[AbstractFeatureAclService.enumerateAssetsWithReadPermissions]: no AclAssetResourceType found for "${this.featureType}"`,
      );
      return of(null);
    }

    return this.checkAreAllResourcesReadable().pipe(
      switchMap((areAllResourcesReadable) =>
        areAllResourcesReadable
          ? of(null)
          : this.assetAclService.enumerateAssets({
              resourceType,
              permissions: [AclAssetPermission.Read],
            }),
      ),
      map(() => null),
    );
  }

  checkAssetPermission({
    permission,
    strategy,
  }: {
    permission: AclPermission;
    strategy?: AclStrategy;
  }): Observable<AclPermissionDetail> {
    return this.checkAssetPermissions({
      permissions: [permission],
      strategy,
    }).pipe(map((permissionsMap) => permissionsMap[permission] || { allowed: false }));
  }

  checkAssetPermissions({
    permissions,
    strategy,
  }: {
    permissions: AclPermission[];
    strategy?: AclStrategy;
  }): Observable<AclPermissionsRecord> {
    if (!this.assetResourceType) {
      console.warn(
        `[AbstractFeatureAclService.checkAssetPermissions]: no AclResourceType found for "${this.featureType}"`,
      );
      return of({} as AclPermissionsRecord);
    }

    const assetPath = this.query.getActive()?.$path;

    if (isNullish(assetPath)) {
      this.errorHandlerService.error('[AbstractFeatureAclService.checkAssetPermissions]: nullish assetPath');
      return of({} as AclPermissionsRecord);
    }

    return this.assetAclService.checkAssetPermissions({
      resourceType: this.assetResourceType,
      assetPath,
      permissions,
      strategy,
    });
  }

  checkCanEditAsset({ strategy }: { strategy?: AclStrategy } = {}): Observable<AclPermissionDetail> {
    return this.checkAssetPermission({
      permission: AclAssetPermission.Write,
      strategy,
    });
  }

  private checkAreAllResourcesReadable(): Observable<boolean> {
    const resourceType = this.assetModuleResourceType;

    if (!resourceType) {
      console.warn(
        `[AbstractFeatureAclService.checkAreAllResourcesReadable]: no AclAssetModuleResourceType found for "${this.featureType}"`,
      );
      return of(false);
    }

    return this.orgSpaceAclService.checkOrgSpacePermission({ permission: AclOrgSpacePermission.ReadAllResources }).pipe(
      switchMap((areAllSpaceResourcesReadable) =>
        areAllSpaceResourcesReadable.allowed
          ? of(areAllSpaceResourcesReadable)
          : this.assetModuleAclService.checkAssetModulePermission({
              resourceType,
              permission: AclAssetModulePermission.ReadAllResources,
            }),
      ),
      map(({ allowed }) => allowed),
    );
  }
}
